// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.2.0"
//
// Changelog:
//
// Version 1.2.0:
// - [Changed] Updated the component to distinguish between subscribing to a new
//   plan and changing an existing one. The `onSelectPlan` handler now correctly
//   initiates the new preview workflow for plan changes.
//
// Version 1.1.0:
// - [Added] A small informational text hint to inform users that discount codes
//   can be applied on the Stripe-hosted checkout page.
// ==============================================================================
// File: src/features/subscriptions/components/SubscriptionPlanCard.tsx
import React from 'react';
import { SubscriptionPlan, BillingIntervalEnum, CoachSubscription, SubscriptionStatusEnum } from '../../../types';
import Button from '../../../components/common/Button';
import { CheckIcon } from '@heroicons/react/24/solid';

interface SubscriptionPlanCardProps {
  plan: SubscriptionPlan;
  onSelectPlan: (plan: SubscriptionPlan, interval: BillingIntervalEnum) => void;
  currentSubscription: CoachSubscription | null;
  isLoading: boolean;
  processingId: string | null;
}

const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({ plan, onSelectPlan, currentSubscription, isLoading, processingId }) => {
  const isMonthlyVisible = plan.billing_interval_options.includes(BillingIntervalEnum.Monthly) && plan.price_monthly;
  const isYearlyVisible = plan.billing_interval_options.includes(BillingIntervalEnum.Yearly) && plan.price_yearly;

  const isMonthlyPurchasable = isMonthlyVisible && plan.stripe_price_id_monthly;
  const isYearlyPurchasable = isYearlyVisible && plan.stripe_price_id_yearly;

  const isTheCurrentPaidPlan = currentSubscription?.plan_id === plan.id && currentSubscription?.status === SubscriptionStatusEnum.Active;

  const yearlySavingsPercentage = React.useMemo(() => {
    if (plan.price_monthly && plan.price_yearly) {
      const monthlyPrice = parseFloat(String(plan.price_monthly));
      const yearlyPrice = parseFloat(String(plan.price_yearly));
      if (monthlyPrice > 0) {
        const savings = (monthlyPrice * 12) - yearlyPrice;
        return Math.round((savings / (monthlyPrice * 12)) * 100);
      }
    }
    return 0;
  }, [plan.price_monthly, plan.price_yearly]);

  const renderActionArea = () => {
    if (isTheCurrentPaidPlan) {
      return (
        <div className="text-center font-semibold text-primary p-2 border-2 border-dashed border-primary rounded-md">
          Current Plan
        </div>
      );
    }
    
    const actionText = currentSubscription ? 'Change Plan' : 'Choose Monthly';

    return (
      <>
        {isMonthlyVisible && (
          <Button 
            onClick={() => onSelectPlan(plan, BillingIntervalEnum.Monthly)} 
            fullWidth
            isLoading={isLoading && processingId === plan.stripe_price_id_monthly}
            disabled={isLoading || !isMonthlyPurchasable}
            title={!isMonthlyPurchasable ? "This plan is not configured for purchase." : actionText}
          >
            {actionText}
          </Button>
        )}
        {isYearlyVisible && (
          <Button 
            onClick={() => onSelectPlan(plan, BillingIntervalEnum.Yearly)} 
            fullWidth
            variant="outline"
            isLoading={isLoading && processingId === plan.stripe_price_id_yearly}
            disabled={isLoading || !isYearlyPurchasable}
            title={!isYearlyPurchasable ? "This plan is not configured for purchase." : "Choose Yearly Plan"}
          >
            <span className="flex items-center justify-center">
                {currentSubscription ? 'Change to Yearly' : 'Choose Yearly'}
                {yearlySavingsPercentage > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                        Save {yearlySavingsPercentage}%!
                    </span>
                )}
            </span>
          </Button>
        )}
        {!isMonthlyVisible && !isYearlyVisible && (
            <p className="text-center text-sm text-neutral-500">This plan is not currently available for purchase.</p>
        )}
      </>
    );
  };

  return (
    <div className={`border rounded-lg shadow-lg flex flex-col h-full p-6 min-h-[34rem] ${isTheCurrentPaidPlan ? 'border-primary border-2' : 'border-neutral-light'}`}>
      <div className="p-6">
        <h3 className="text-2xl font-bold text-neutral-darkest text-center">{plan.name}</h3>
        <div className="mt-2 text-sm text-neutral-dark text-center h-[80px] overflow-hidden">
            <p>{plan.description}</p>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-4xl font-extrabold text-neutral-darkest">
            ${plan.price_monthly}
            <span className="text-base font-medium text-neutral-dark">/month</span>
          </p>
          {isYearlyVisible && (
            <p className="text-sm text-neutral-DEFAULT mt-1">
              (or ${plan.price_yearly}/year)
            </p>
          )}
        </div>
      </div>
      
      <div className="flex-grow p-6 bg-neutral-lightest border-t border-neutral-light space-y-4">
        <p className="font-semibold text-neutral-darkest">Includes:</p>
        <ul className="space-y-2 text-sm text-neutral-dark">
          <li className="flex items-start">
            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span><span className="font-bold">{plan.seats_provided}</span> Client Seats</span>
          </li>
          <li className="flex items-start">
            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span>Full Access to All Coaching Tools</span>
          </li>
          <li className="flex items-start">
            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span>Client & Appointment Management</span>
          </li>
        </ul>
      </div>
      
      <div className="p-6 bg-white rounded-b-lg space-y-3">
        <p className="text-xs text-center text-neutral-dark">
            Have a discount code? You can apply it on the next step.
        </p>
        {renderActionArea()}
      </div>
    </div>
  );
};

export default SubscriptionPlanCard;